import {inject, Injectable} from '../di';
import {TV_VENDOR_DETECTION_CONFIG} from './tv-vendor-detection-config';
import {TvVendor} from './tv-vendor';
import {Logger} from '../logger';
import {AppInitializeService} from '../app-initialize-service';
import {TvVendorModule} from './tv-vendor-module';
import {TvVendorStorage} from './tv-vendor-storage';
import {TvVendorKeys} from './tv-vendor-keys';
import {TvVendorPlayer} from './tv-vendor-player';
import {TvVendorAppManager} from './tv-vendor-app-manager';
import {CustomKeyType, KeyType} from '../../types/key-type';
import {LegacyLogService} from '../logger/legacy-log-service';
import {getGlobalScope} from '@sentry/browser';

/**
 * A service responsible for determining the manufacturer (vendor) of the current TV and loading the appropriate
 * modules to work with this vendor.
 */
@Injectable()
export class TvVendorService implements AppInitializeService {
  private readonly logger = inject(Logger);

  private readonly userAgent: string = navigator.userAgent.toLowerCase();

  public readonly vendor: TvVendor = this.detectTvVendor();

  private module?: TvVendorModule;

  get storage(): TvVendorStorage | undefined {
    return this.module?.storage;
  }

  get keys(): TvVendorKeys | undefined {
    return this.module?.keys;
  }

  get player(): TvVendorPlayer | undefined {
    return this.module?.player;
  }

  get appManager(): TvVendorAppManager | undefined {
    return this.module?.appManager;
  }

  constructor() {
    // todo legacy
    window.Vendor = {
      get: () => this.vendor,
      sendKey: (key: KeyType | CustomKeyType) => this.sendKey(key),
    };

    window.tv_amino_mark = 0;
    window.tv_lg_mark = 0;
    window.tv_samsung_mark = 0;
    window.tv_samsung_tizen_mark = 0;
    window.tv_tizen_mark = 0;
    window.tv_philips_old_mark = 0;
    window.tv_philips_mark = 0;
    window.tv_mag_mark = 0;
    window.tv_tvip_mark = 0;
    window.tv_sony_mark = 0;
    window.tv_vestel_mark = 0;
    window.tv_desktop_mark = 0;
    window.tv_manufacturer = '';
  }

  public async init(): Promise<boolean> {
    this.logger.log('[App] UserAgent: ' + this.userAgent);
    this.logger.log('[App] TV vendor: ' + this.vendor);

    try {
      // Allow `log.add` usage in legacy code in vendor modules, before `LegacyLogService` initialization.
      // Remove this line after vendor modules migration to new logger.
      inject(LegacyLogService);

      const cfg = TV_VENDOR_DETECTION_CONFIG[this.vendor];
      const module = await cfg.moduleResolver();
      this.module = module;

      const initResult = await module.init();

      if (!initResult) {
        this.logger.error(`[TvVendorService] initResult: ${initResult}, ${JSON.stringify(module)}`);
        return false;
      }

      await this.legacyVendorModuleInit();

      // TODO: legacy, will be resolved in feature/vendor-common-methods-1755807940
      $(HotezaTV).on('splashshow', () => {
        setTimeout(() => {
          // Merge custom keys into tv_keys (Peninsula custom keys)
          if (window.tv_custom_keys) {
            Object.assign(window.tv_keys, window.tv_custom_keys);
          }

          //keys dict for fast search
          window.tv_keys_reverse = {};
          for (const key in window.tv_keys) {
            window.tv_keys_reverse[window.tv_keys[key]] = key;
          }
        }, 2000);
      });

      this.setSentryTags();

      this.logger.debug(`[TvVendorService] TV ${this.vendor} vendor module initialized successfully`);

      return true;
    } catch (err) {
      this.logger.critical(`[TvVendorService] Unable to load and initialize ${this.vendor} Vendor Module`, err);
      return false;
    }
  }

  /**
   * Sends a key press to the TV.
   * For standard keys when a native sendKey function is available, uses that function.
   * For custom keys or when no native function exists, triggers a jQuery keydown event.
   * @param key - The key to send (standard KeyType or CustomKeyType)
   */
  public sendKey(key: KeyType | CustomKeyType): void {
    this.logger.debug(`[TvVendorService] sendKey: ${key}`);
    const isStandardKey = Object.values(KeyType).includes(key as KeyType);

    if (isStandardKey && typeof window.__sendKey === 'function') {
      window.__sendKey(key).catch((e) => {
        this.logger.error('[TvVendorService] sendKey: native sendKey failed', e);
      });
    } else {
      const keyCode = window.tv_keys[key];
      if (keyCode === undefined) {
        this.logger.warn(`[TvVendorService] sendKey: unknown key "${key}"`);
        return;
      }

      try {
        $(document).trigger({type: 'keydown', keyCode});
      } catch (e) {
        this.logger.error('[TvVendorService] sendKey: error during press', e);
      }
    }
  }

  /**
   * Detect current TV vendor
   */
  private detectTvVendor() {
    let maxProbesNum = 0;
    let vendor: TvVendor = TvVendor.Desktop;

    for (const [vendorName, config] of Object.entries(TV_VENDOR_DETECTION_CONFIG)) {
      const positiveProbesNum = config.probes.filter((probe) => this.userAgent.includes(probe.toLowerCase())).length;
      if (positiveProbesNum > maxProbesNum) {
        maxProbesNum = positiveProbesNum;
        vendor = vendorName as TvVendor;
      }
    }

    return vendor;
  }

  private async legacyVendorModuleInit() {
    if (!window._tv_vendor_init || typeof window._tv_vendor_init !== 'function') {
      return Promise.reject(new Error(`Vendor module has no "_tv_vendor_init" function`));
    }

    await new Promise((resolve, reject) => {
      window._tv_vendor_init().done(resolve).fail(reject);
    });

    window.tv_manufacturer = this.vendor;

    //TODO: del
    //COMPATIBILITY
    window[`tv_${this.vendor}_mark`] = 3;

    if (this.vendor === TvVendor.Tvip) {
      window.tv_mag_mark = 2;
    } else if (this.vendor === TvVendor.Tizen) {
      window.tv_samsung_mark = 2;
      window.tv_samsung_tizen_mark = 3;
      window.tv_manufacturer = 'samsung';
    } else if (this.vendor === TvVendor.TizenTep) {
      window.tv_samsung_mark = 2;
      window.tv_samsung_tizen_mark = 3;
      window.tv_manufacturer = 'samsung';
    }

    // TODO: refactor away to screen size service
    if (window.__tv_set_window_size && typeof window.__tv_set_window_size === 'function') {
      const windowSize: 'hd720upscaled' | 'hd720' = await Promise.resolve(window.__tv_set_window_size());

      if (windowSize === 'hd720upscaled') {
        $('html').addClass('hd720upscaled');
        this.logger.debug(`[App] Window size: "hd720upscaled" (Full HD)`);
      } else {
        $('html').addClass('hd720');
        this.logger.debug(`[App] Window size: "hd720"`);
      }
    } else {
      $('html').addClass('hd720');
      this.logger.debug(`[App] Window size: "hd720"`);
    }

    //-------------
  }

  /**
   * Set TV info (firmware, serial, etc.) into Sentry tags.
   */
  private setSentryTags() {
    try {
      getGlobalScope().setTag('tv.vendor', this.vendor);

      if (!(window.tv_get_info && typeof window.tv_get_info === 'function')) {
        return;
      }

      const tvInfo = window.tv_get_info() as Record<string, number | string | boolean | null | undefined>;
      if (!tvInfo) {
        return;
      }

      Object.entries(tvInfo).forEach(([key, value]) => {
        if (value != null && value.toString) {
          // Tag values have a maximum length of 200 characters and they cannot contain the newline (\n) character.
          value = value
            .toString()
            .trim()
            .replace(/[\n\r]+/g, ' ')
            .slice(0, 200);
          getGlobalScope().setTag('tv.' + key, value);
        }
      });
    } catch (err) {
      this.logger.warn('[TvVendorService] Unable to get TV info', err);
    }
  }
}
